/**
 * Parrainage : code, statut Ambassadeur et progression. Toute la logique de
 * récompense reste côté serveur (ReferralService). La remise Apple ne
 * s'active que depuis l'iPhone : le site se contente de le dire.
 */
import React, { useEffect, useState } from 'react';

function h() { return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` }; }

interface Infos {
  code: string;
  lien: string;
  est_ambassadeur: boolean;
  seuil_ambassadeur: number;
  nb_filleuls_actifs: number;
  remise_apple_a_activer?: boolean;
  plateforme_paiement?: string | null;
}

export default function ParrainagePage() {
  const [infos, setInfos] = useState<Infos | null>(null);
  const [erreur, setErreur] = useState(false);
  const [copie, setCopie] = useState('');

  useEffect(() => {
    fetch('/api/referral/me', { headers: h() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setInfos)
      .catch(() => setErreur(true));
  }, []);

  const copier = async (texte: string, quoi: string) => {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(quoi);
      setTimeout(() => setCopie(''), 2000);
    } catch { /* presse-papiers refusé par le navigateur : le texte reste sélectionnable */ }
  };

  if (erreur) return <p className="text-sm text-gray-500">Parrainage indisponible pour le moment.</p>;
  if (!infos) return <p className="text-sm text-gray-500">Chargement…</p>;

  const seuil = infos.seuil_ambassadeur || 3;
  const progres = Math.min(100, (infos.nb_filleuls_actifs / seuil) * 100);
  const statut = !infos.est_ambassadeur
    ? `À partir de ${seuil} filleuls avec un abonnement actif en même temps, votre abonnement EvoClick passe à moitié prix.`
    : infos.remise_apple_a_activer
      ? "Bravo ! Activez votre remise depuis l'app sur iPhone : votre abonnement App Store passe à moitié prix dès son prochain renouvellement, pendant 12 mois."
      : infos.plateforme_paiement === 'apple'
        ? `Votre remise -50 % est active sur votre abonnement App Store pour 12 mois. Elle vous sera reproposée ensuite si vous avez toujours ${seuil} filleuls abonnés.`
        : `Votre abonnement EvoClick est à moitié prix tant que vous gardez au moins ${seuil} filleuls avec un abonnement actif.`;

  const etapes = [
    'Partagez votre code avec un confrère ou une consœur.',
    "Il ou elle s'inscrit avec votre code.",
    'Dès son premier paiement, il ou elle compte parmi vos filleuls actifs.',
    `À ${seuil} filleuls actifs, votre abonnement passe à moitié prix.`,
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
        <p className="text-sm text-gray-500">Votre code de parrainage</p>
        <p className="text-2xl font-bold tracking-widest text-violet-700 mt-1 break-all">{infos.code}</p>
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          <button onClick={() => copier(infos.code, 'code')} className="px-4 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700">{copie === 'code' ? 'Code copié' : 'Copier le code'}</button>
          {infos.lien && <button onClick={() => copier(infos.lien, 'lien')} className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">{copie === 'lien' ? 'Lien copié' : 'Copier le lien'}</button>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-gray-900">Statut Ambassadeur</p>
          {infos.est_ambassadeur && <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">ACTIF</span>}
        </div>
        <p className="text-sm text-gray-500 mt-2">{statut}</p>
        <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden mt-4">
          <div className={`h-full ${infos.est_ambassadeur ? 'bg-amber-500' : 'bg-violet-600'}`} style={{ width: `${progres}%` }} />
        </div>
        <p className="text-xs font-semibold text-gray-500 mt-2">{infos.nb_filleuls_actifs} / {seuil} filleuls actifs</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <p className="font-semibold text-gray-900 mb-3">Comment ça marche</p>
        <ol className="space-y-2">
          {etapes.map((e, i) => (
            <li key={e} className="flex gap-3 text-sm text-gray-700">
              <span className="w-6 h-6 shrink-0 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
              {e}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
